import React, { useState, useEffect } from 'react';
import { scheduleAPI, workoutsAPI } from '../../api';
import { 
  Calendar, Clock, Plus, ChevronLeft, ChevronRight, Bell, 
  Check, X, Repeat, Play, Trash2, Edit, AlertCircle
} from 'lucide-react';

const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const RECURRENCE_OPTIONS = [
  { value: 'none', label: 'No repeat' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'custom', label: 'Custom days' },
];

export function WorkoutScheduler({ userRole, onStartWorkout }) {
  const [weekOffset, setWeekOffset] = useState(0);
  const [weekData, setWeekData] = useState(null);
  const [todaysWorkouts, setTodaysWorkouts] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    fetchData();
  }, [weekOffset]);

  useEffect(() => {
    // Poll for reminders every minute
    const interval = setInterval(() => {
      scheduleAPI.getPendingReminders().then(res => setReminders(res.data));
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [weekRes, todayRes, remindersRes, templatesRes] = await Promise.all([
        scheduleAPI.getWeeklySchedule(weekOffset),
        scheduleAPI.getTodaysWorkouts(),
        scheduleAPI.getPendingReminders(),
        workoutsAPI.listTemplates()
      ]);
      setWeekData(weekRes.data);
      setTodaysWorkouts(todayRes.data);
      setReminders(remindersRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      console.error('Error fetching schedule data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkComplete = async (scheduleId) => {
    try {
      await scheduleAPI.markComplete(scheduleId);
      fetchData();
    } catch (error) {
      console.error('Error marking complete:', error);
    }
  };

  const handleDelete = async (scheduleId) => {
    if (!window.confirm('Delete this scheduled workout?')) return;
    try {
      await scheduleAPI.deleteSchedule(scheduleId);
      fetchData();
    } catch (error) {
      console.error('Error deleting schedule:', error);
    }
  };

  const handleDismissReminder = async (reminderId) => {
    try {
      await scheduleAPI.markReminderRead(reminderId);
      setReminders(prev => prev.filter(r => r.id !== reminderId));
    } catch (error) {
      console.error('Error dismissing reminder:', error);
    }
  };

  const getWeekDates = () => {
    if (!weekData) return [];
    const start = new Date(weekData.week_start);
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(start);
      date.setDate(date.getDate() + i);
      return date;
    });
  };

  const getWorkoutsForDate = (date) => {
    if (!weekData) return [];
    const dateStr = date.toISOString().split('T')[0];
    return weekData.scheduled_workouts.filter(w => {
      const wDate = new Date(w.scheduled_date).toISOString().split('T')[0];
      return wDate === dateStr;
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="workout-scheduler">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Workout Schedule</h1>
          <p className="text-gray-600">Plan and schedule your workouts</p>
        </div>
        <button
          onClick={() => { setEditingSchedule(null); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
          data-testid="schedule-workout-btn"
        >
          <Plus className="w-5 h-5" />
          Schedule Workout
        </button>
      </div>

      {/* Pending Reminders */}
      {reminders.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Bell className="w-5 h-5 text-amber-600" />
            <h3 className="font-semibold text-amber-800">Workout Reminders</h3>
          </div>
          <div className="space-y-2">
            {reminders.map(reminder => (
              <div key={reminder.id} className="flex items-center justify-between bg-white p-3 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{reminder.workout_name}</p>
                  <p className="text-sm text-gray-500">
                    Scheduled for {reminder.scheduled_time}
                  </p>
                </div>
                <div className="flex gap-2">
                  {onStartWorkout && (
                    <button
                      onClick={() => onStartWorkout(reminder)}
                      className="px-3 py-1 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
                    >
                      Start Now
                    </button>
                  )}
                  <button
                    onClick={() => handleDismissReminder(reminder.id)}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Today's Workouts */}
      {todaysWorkouts.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-indigo-600" />
            Today's Workouts
          </h2>
          <div className="space-y-3">
            {todaysWorkouts.map(workout => (
              <TodayWorkoutCard
                key={workout.id}
                workout={workout}
                onComplete={() => handleMarkComplete(workout.id)}
                onStart={onStartWorkout}
              />
            ))}
          </div>
        </div>
      )}

      {/* Week View */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Week Navigation */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <button
            onClick={() => setWeekOffset(prev => prev - 1)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="text-center">
            <p className="font-semibold text-gray-900">
              {weekData && new Date(weekData.week_start).toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric' 
              })} - {weekData && new Date(weekData.week_end).toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric',
                year: 'numeric'
              })}
            </p>
            <p className="text-sm text-gray-500">
              {weekData?.completed_count || 0} completed • {weekData?.pending_count || 0} pending
            </p>
          </div>
          <button
            onClick={() => setWeekOffset(prev => prev + 1)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Week Grid */}
        <div className="grid grid-cols-7 divide-x divide-gray-100">
          {getWeekDates().map((date, idx) => {
            const workouts = getWorkoutsForDate(date);
            const today = isToday(date);
            
            return (
              <div
                key={idx}
                className={`min-h-[200px] p-2 ${today ? 'bg-indigo-50' : ''}`}
              >
                <div className="text-center mb-2">
                  <p className="text-xs text-gray-500">{DAYS_OF_WEEK[idx]}</p>
                  <p className={`text-lg font-semibold ${today ? 'text-indigo-600' : 'text-gray-900'}`}>
                    {date.getDate()}
                  </p>
                </div>
                <div className="space-y-1">
                  {workouts.map(workout => (
                    <ScheduleCard
                      key={workout.id}
                      workout={workout}
                      compact
                      onEdit={() => { setEditingSchedule(workout); setShowForm(true); }}
                      onDelete={() => handleDelete(workout.id)}
                      onComplete={() => handleMarkComplete(workout.id)}
                    />
                  ))}
                  <button
                    onClick={() => { setSelectedDate(date); setShowForm(true); }}
                    className="w-full p-1 text-xs text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                  >
                    + Add
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Schedule Form Modal */}
      {showForm && (
        <ScheduleForm
          schedule={editingSchedule}
          templates={templates}
          defaultDate={selectedDate}
          onClose={() => { setShowForm(false); setEditingSchedule(null); setSelectedDate(null); }}
          onSave={() => { setShowForm(false); setEditingSchedule(null); setSelectedDate(null); fetchData(); }}
        />
      )}
    </div>
  );
}

function TodayWorkoutCard({ workout, onComplete, onStart }) {
  return (
    <div className={`flex items-center justify-between p-4 rounded-lg ${
      workout.is_completed ? 'bg-green-50' : 'bg-gray-50'
    }`}>
      <div className="flex items-center gap-3">
        {workout.is_completed ? (
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
            <Check className="w-5 h-5 text-green-600" />
          </div>
        ) : (
          <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
            <Clock className="w-5 h-5 text-indigo-600" />
          </div>
        )}
        <div>
          <p className="font-medium text-gray-900">{workout.workout_name}</p>
          <p className="text-sm text-gray-500">
            {workout.scheduled_time} • {workout.duration_minutes} min
          </p>
        </div>
      </div>
      {!workout.is_completed && (
        <div className="flex gap-2">
          {onStart && (
            <button
              onClick={() => onStart(workout)}
              className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
            >
              <Play className="w-4 h-4" />
              Start
            </button>
          )}
          <button
            onClick={onComplete}
            className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded-lg"
          >
            Mark Done
          </button>
        </div>
      )}
    </div>
  );
}

function ScheduleCard({ workout, compact, onEdit, onDelete, onComplete }) {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div
      className={`relative p-2 rounded text-xs ${
        workout.is_completed 
          ? 'bg-green-100 text-green-800' 
          : 'bg-indigo-100 text-indigo-800'
      }`}
      data-testid={`schedule-card-${workout.id}`}
    >
      <div className="flex items-start justify-between gap-1">
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{workout.workout_name}</p>
          <p className="text-[10px] opacity-75">{workout.scheduled_time}</p>
        </div>
        {workout.recurrence !== 'none' && (
          <Repeat className="w-3 h-3 flex-shrink-0" />
        )}
      </div>
      
      {!compact && (
        <div className="flex gap-1 mt-1">
          {!workout.is_completed && (
            <button onClick={onComplete} className="p-0.5 hover:bg-white/50 rounded">
              <Check className="w-3 h-3" />
            </button>
          )}
          <button onClick={onEdit} className="p-0.5 hover:bg-white/50 rounded">
            <Edit className="w-3 h-3" />
          </button>
          <button onClick={onDelete} className="p-0.5 hover:bg-white/50 rounded">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}

function ScheduleForm({ schedule, templates, defaultDate, onClose, onSave }) {
  const [formData, setFormData] = useState({
    template_id: schedule?.template_id || '',
    workout_name: schedule?.workout_name || '',
    scheduled_date: schedule?.scheduled_date 
      ? new Date(schedule.scheduled_date).toISOString().split('T')[0]
      : defaultDate 
        ? defaultDate.toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
    scheduled_time: schedule?.scheduled_time || '09:00',
    duration_minutes: schedule?.duration_minutes || 60,
    notes: schedule?.notes || '',
    recurrence: schedule?.recurrence || 'none',
    recurrence_days: schedule?.recurrence_days || [],
    reminder_enabled: schedule?.reminder_enabled ?? true,
    reminder_minutes_before: schedule?.reminder_minutes_before || 30,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleTemplateChange = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    setFormData(prev => ({
      ...prev,
      template_id: templateId,
      workout_name: template?.name || prev.workout_name,
      duration_minutes: template?.estimated_duration_minutes || prev.duration_minutes
    }));
  };

  const toggleRecurrenceDay = (dayIndex) => {
    setFormData(prev => ({
      ...prev,
      recurrence_days: prev.recurrence_days.includes(dayIndex)
        ? prev.recurrence_days.filter(d => d !== dayIndex)
        : [...prev.recurrence_days, dayIndex].sort()
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.workout_name.trim()) {
      setError('Please enter a workout name');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        ...formData,
        scheduled_date: new Date(formData.scheduled_date).toISOString(),
        recurrence_days: formData.recurrence === 'custom' ? formData.recurrence_days : null
      };

      if (schedule) {
        await scheduleAPI.updateSchedule(schedule.id, payload);
      } else {
        await scheduleAPI.createSchedule(payload);
      }
      onSave();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="schedule-form-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">
              {schedule ? 'Edit Schedule' : 'Schedule Workout'}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Template Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Template</label>
              <select
                value={formData.template_id}
                onChange={(e) => handleTemplateChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg"
              >
                <option value="">Custom workout</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            {/* Workout Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Workout Name *</label>
              <input
                type="text"
                value={formData.workout_name}
                onChange={(e) => setFormData(prev => ({ ...prev, workout_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                data-testid="schedule-name-input"
              />
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, scheduled_date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                <input
                  type="time"
                  value={formData.scheduled_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, scheduled_time: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                />
              </div>
            </div>

            {/* Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
              <input
                type="number"
                value={formData.duration_minutes}
                onChange={(e) => setFormData(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 60 }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg"
              />
            </div>

            {/* Recurrence */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Repeat</label>
              <select
                value={formData.recurrence}
                onChange={(e) => setFormData(prev => ({ ...prev, recurrence: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg"
              >
                {RECURRENCE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Custom Days */}
            {formData.recurrence === 'custom' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Days</label>
                <div className="flex gap-2">
                  {DAYS_OF_WEEK.map((day, idx) => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleRecurrenceDay(idx)}
                      className={`w-10 h-10 rounded-full text-xs font-medium transition ${
                        formData.recurrence_days.includes(idx)
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {day.charAt(0)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Reminder */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="reminder_enabled"
                  checked={formData.reminder_enabled}
                  onChange={(e) => setFormData(prev => ({ ...prev, reminder_enabled: e.target.checked }))}
                  className="rounded"
                />
                <label htmlFor="reminder_enabled" className="text-sm font-medium text-gray-700">
                  Enable reminder
                </label>
              </div>
              {formData.reminder_enabled && (
                <div className="flex items-center gap-2 ml-6">
                  <span className="text-sm text-gray-600">Remind me</span>
                  <select
                    value={formData.reminder_minutes_before}
                    onChange={(e) => setFormData(prev => ({ ...prev, reminder_minutes_before: parseInt(e.target.value) }))}
                    className="px-2 py-1 border border-gray-200 rounded text-sm"
                  >
                    <option value={15}>15 min</option>
                    <option value={30}>30 min</option>
                    <option value={60}>1 hour</option>
                    <option value={120}>2 hours</option>
                  </select>
                  <span className="text-sm text-gray-600">before</span>
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                data-testid="save-schedule-btn"
              >
                {saving ? 'Saving...' : (schedule ? 'Update' : 'Schedule')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default WorkoutScheduler;
